import React, { ReactNode, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import Header from '../homepage/header'
import MainNav from '../homepage/main-nav'
import Button from '../common/button'
import { useUser } from '../../common/hooks/auth/useUser'
import { toast } from 'react-toastify'
import { useTranslation } from '../../useTranslation'
import UpdateAddressModal from '../common/modal/modal-address'
import Footer from '../common/footer'

type AccountLayoutProps = {
    children: ReactNode
    responsive?: boolean
}

const AccountLayout: React.FC<AccountLayoutProps> = ({ children, responsive = false }) => {
    const [showAddressModal, setShowAddressModal] = useState(false)
    const location = useLocation()
    const accountCurrentPage = location.pathname.replace('/account/', '')
    const { removeUser, isVerified, user } = useUser()
    const navigate = useNavigate()

    const { t } = useTranslation()

    const handleLogout = async () => {
        await navigate('/')
        removeUser()
        toast.success(t('youHaveSuccessfullyLoggedOut'))
    }

    const navItems = useMemo(() => {
        const items = [
            {
                title: t('myAccount.navLink.overview'),
                name: 'overview',
                url: '/account/overview',
            },
            {
                title: t('myAccount.navLink.personalData'),
                name: 'details',
                url: '/account/details',
            },
            {
                title: t('myAccount.navLink.addresses'),
                name: 'addresses',
                url: '/account/addresses',
            },
        ]
        if (isVerified && user?.showMetalAccount) {
            items.push({
                title: t('myAccount.navLink.metalAccount'),
                name: 'metal-account',
                url: '/account/metal-account',
            })
        }
        if (isVerified) {
            items.push({
                title: t('myAccount.navLink.orderHistory'),
                name: 'orders',
                url: '/account/orders',
            })
        }
        items.push(
            {
                title: t('myAccount.navLink.legitimation'),
                name: 'authentications',
                url: '/account/authentications',
            },
            {
                title: t('myAccount.navLink.security'),
                name: 'settings',
                url: '/account/settings',
            },
            {
                title: t('myAccount.navLink.configurator'),
                name: 'configurator',
                url: '/account/configurator',
            }
        )

        return items
    }, [isVerified, t])

    return (
        <div className={responsive ? 'responsive' : 'non-responsive'}>
            <Header />
            <MainNav showConfiguratorNav />
            <UpdateAddressModal open={showAddressModal} onClose={() => setShowAddressModal(false)} />
            <div className="container flex justify-between gap-14 mb-5 mt-10 max-tablet:w-full max-tablet:max-w-full max-tablet:px-12">
                <div className="w-1/5 h-fit max-tablet:w-1/4">
                    <div className="shadow-custom-box p-6 max-tablet:px-6 max-tablet:py-7">
                        <h3 className="font-bold mb-3 text-xl leading-none max-tablet:text-base max-tablet:mb-2.5">{t('header.myAccount')}</h3>
                        <ul>
                            {navItems.map((item) => (
                                <Link to={item.url} key={item.name}>
                                    <li
                                        className={`text-xl font-light leading-none mb-3 hover:text-black max-tablet:text-base max-tablet:mb-2.5 ${
                                            item.name === accountCurrentPage ? 'text-black' : 'text-[#d4d4d4]'
                                        }`}
                                    >
                                        {item.title}
                                    </li>
                                </Link>
                            ))}
                        </ul>
                        <div className="mt-6">
                            <Button type={['squareBlue', 'full']} icon="right-from-bracket" text={t('logout')} onClick={handleLogout} />
                        </div>
                    </div>
                    {accountCurrentPage === 'addresses' && (
                        <div className="mb-5 py-4 px-5 text-center box-border w-full bg-gray-100 mt-10">
                            <Button
                                type="squareBlue"
                                icon="plus"
                                text={t('myAccount.addresses.addNewDeliveryAddress')}
                                onClick={() => setShowAddressModal(true)}
                            />
                        </div>
                    )}

                    {accountCurrentPage === 'details' && (
                        <div className="mb-5 w-full p-4 bg-gray-100 mt-10 max-tablet:p-3">
                            <p className="text-custom_yellow max-tablet:text-sm">* {t('mandatoryField')}</p>
                        </div>
                    )}
                </div>

                <div className="w-4/5 relative max-tablet:w-3/4">{children}</div>
            </div>
            <Footer />
        </div>
    )
}

export default AccountLayout
